import React, { useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Form, FormFields, areObjectsEqual } from "sprint-common-web";

import { CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID } from "../../../../../../common/api/master-api/common/consts";
import { ContentReferenceType } from "../../../../../../common/types/ContentReferenceType";
import { RecordType } from "../../../../../../common/types/RecordType";
import { CloudFileDetails } from "../../../CloudFileDetails";
import { useCloudFileDashboard } from "../../../common/context/useCloudFileDashboard";
import { useCloudFileDetails } from "../../../common/useCloudFileDetails";
import { CloudFileAssociationFormState } from "../context/CloudFileAssociationContext";
import { useCloudFileAssociationSection } from "../context/useCloudFileAssociationSection";

type CloudFileAssociationFormProps = {
    children: React.ReactNode;
};

export const CloudFileAssociationForm = ({ children }: CloudFileAssociationFormProps): JSX.Element => {
    const { isContextReady, defaultFormValues, setDefaultFormValues } = useCloudFileAssociationSection();
    const { setAreUnsavedChanges, lookupFileRecordId } = useCloudFileDashboard();

    const { data: cloudFileDetails } = useCloudFileDetails();

    const isFormReady = !!cloudFileDetails && isContextReady;

    const form = useForm<CloudFileAssociationFormState>({
        mode: "all",
        shouldUnregister: false
    });

    useEffect(() => {
        if (!isFormReady) return;

        const values = getDefaults({
            cloudFileDetails
        });

        form.reset(values);

        setDefaultFormValues(values);
    }, [isFormReady, lookupFileRecordId]);

    const currentValues = useWatch({ control: form.control });

    useEffect(() => {
        if (!isFormReady) return;

        const hasChanges = !areObjectsEqual(currentValues, defaultFormValues);
        setAreUnsavedChanges(hasChanges);
    }, [currentValues, defaultFormValues]);

    return (
        <FormProvider {...form}>
            <Form>
                <FormFields>{children}</FormFields>
            </Form>
        </FormProvider>
    );
};

interface GetDefaultsProps {
    cloudFileDetails?: CloudFileDetails;
}

const getDefaults = ({ cloudFileDetails }: GetDefaultsProps): CloudFileAssociationFormState => ({
    selectedLanguages: {
        soundtrackLanguageId: cloudFileDetails?.languages.soundtrackLanguageId
            ? cloudFileDetails.languages.soundtrackLanguageId
            : null,
        subtitleLanguageId:
            cloudFileDetails?.languages.subtitleLanguageId ??
            (cloudFileDetails?.recordType === RecordType.Video ? CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID : null)
    },
    selectedVideoTechnicalDetails: {
        aspectRatioId: cloudFileDetails?.videoTechnical?.aspectRatio?.id ?? null,
        aspectTypeId: cloudFileDetails?.videoTechnical?.aspectType?.id ?? null
    },
    ...getContent(cloudFileDetails)
});

const getContent = (cloudFileDetails?: CloudFileDetails) => {
    const baseContent = {
        contentType: undefined,
        contentId: undefined,
        versionIds: [],
        //TODO: change properties below to undefined after react hook form version bump, then reset with keepDefaultValues option (?)
        seasonId: "",
        episodeId: "",
        airlineId: ""
    };

    if (!cloudFileDetails?.content) {
        return baseContent;
    }

    const content = cloudFileDetails.content;

    switch (content.type) {
        case ContentReferenceType.Movie:
            return {
                ...baseContent,
                contentType: content.type,
                contentId: content.movie.id,
                versionIds: content.versions.map((version) => version.id)
            };
        case ContentReferenceType.TvSeries:
            return {
                ...baseContent,
                contentType: content.type,
                contentId: content.tvSeries.id,
                versionIds: content.versions.map((version) => version.id),
                seasonId: content.season?.id,
                episodeId: content.episode?.id
            };
        case ContentReferenceType.Ancillary:
            return {
                ...baseContent,
                contentType: content.type,
                contentId: content.ancillary.id,
                airlineId: content.airline.id,
                versionIds: content.versionSummary.ids
            };

        default:
            return baseContent;
    }
};
